import { withTimeout } from './withTimeout';
import { trace } from './traceLog';

/**
 * Phase 2a (2026-09-29) — Browse load recovery. Two small pieces extracted
 * from LatestPedals so the failure/empty distinction is unit-testable.
 * Deliberately Browse-only for now: no other page migrates onto this until
 * that expansion is reviewed.
 *
 * Background: Browse already had a whole-loader timeout plus one silent
 * retry, but its terminal state on double failure was the LEGITIMATE
 * empty-state UI — after an offline blip the marketplace claimed to be
 * empty ("No pedals available at the moment.") with no way to recover short
 * of a full refresh. The fix is not more retrying; it is telling the truth:
 * a load that FAILED must render as failed, and recover on reconnect.
 */

/**
 * Run a load attempt with a hard timeout and exactly one delayed retry —
 * the exact control flow LatestPedals.loadFeaturedPedals used inline — and
 * report the honest outcome instead of swallowing it:
 *
 *   'ok'         attempt (or its one retry) completed. A zero-result load
 *                is still 'ok' — rendering the legitimate empty state is
 *                the CALLER's decision, made only on 'ok'.
 *   'failed'     both attempts failed while this load was still current.
 *                The caller must show its couldn't-load state, never the
 *                marketplace-empty message.
 *   'superseded' the caller's token moved on (user changed filters or
 *                navigated); the failure belongs to a dead fetch and must
 *                not surface anywhere.
 *
 * The timeout does NOT cancel the underlying Firestore work (see
 * withTimeout.js); callers keep their token/dedupe guards for zombie
 * resolves, exactly as before.
 *
 * @param {Function} makeAttempt  () => Promise — a FRESH attempt per call.
 * @param {object}   opts  { timeoutMs, retryDelayMs, isCurrent, label }
 * @returns {Promise<'ok'|'failed'|'superseded'>}
 */
export async function runWithTimeoutAndRetry(makeAttempt, { timeoutMs, retryDelayMs, isCurrent, label }) {
  try {
    await withTimeout(makeAttempt(), timeoutMs, label);
    return 'ok';
  } catch (error) {
    console.warn(`${label}: first attempt failed, retrying once`, error);
    if (!isCurrent()) return 'superseded';
    await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
    if (!isCurrent()) return 'superseded';
    try {
      await withTimeout(makeAttempt(), timeoutMs, `${label}:retry`);
      return 'ok';
    } catch (retryErr) {
      console.error(`${label}: retry also failed`, retryErr);
      if (!isCurrent()) return 'superseded';
      trace('load_failed', { label });
      return 'failed';
    }
  }
}

/**
 * Arm ONE-SHOT recovery for a load that ended in 'failed': the first signal
 * that connectivity plausibly returned — the browser's 'online' event, or
 * the tab becoming visible again — disarms the listeners FIRST and then
 * runs the recovery exactly once. One-shot by construction, so overlapping
 * online/visibility/manual signals can never stack concurrent loads: a
 * second signal finds nothing armed, and if the recovery run fails again,
 * the caller's failed state re-arms a fresh one-shot.
 *
 * Returns disarm(); callers MUST invoke it on unmount / when the failed
 * state clears, so no listener outlives the page.
 */
/**
 * The false-empty guard (found in localhost validation, 2026-09-29): unlike
 * getDoc — which REJECTS with "client is offline" — a getDocs QUERY on an
 * offline client resolves SUCCESSFULLY with an empty cache-served snapshot
 * (this app uses the default in-memory cache, so after a reload there is
 * nothing cached). Without this guard Browse's loader "succeeded" with zero
 * pedals during an outage, reported 'ok', and rendered the legitimate
 * marketplace-empty message — the exact disguise Phase 2a exists to remove.
 *
 * Rules:
 * - fromCache AND empty  → throw (unconfirmed; retry/failed path decides).
 * - fromCache with data  → pass: stale cards beat a false error.
 * - server-confirmed empty (fromCache false) → pass: genuinely empty.
 */
export function throwIfOfflineEmpty(snapshot, label) {
  if (snapshot.metadata?.fromCache && snapshot.empty) {
    const err = new Error(
      `${label}: query returned an empty CACHE result while offline — unconfirmed, not an empty collection`
    );
    err.name = 'OfflineResultError';
    throw err;
  }
}

/**
 * Document flavor of the same guard (Phase 2b, PedalDetails): a getDoc that
 * "misses" FROM CACHE while offline says "I can't see the doc", not "the
 * listing was deleted". Treating it as deleted would show a false "listing
 * no longer available" page for every offline blip. Only a server-confirmed
 * miss (fromCache false) may drive the real not-found state.
 */
export function throwIfOfflineMiss(docSnap, label) {
  if (!docSnap.exists() && docSnap.metadata?.fromCache) {
    const err = new Error(
      `${label}: document cache-miss while offline — unconfirmed, not a deleted document`
    );
    err.name = 'OfflineResultError';
    throw err;
  }
}

export function armRecoveryOnReconnect(run) {
  let armed = true;
  const disarm = () => {
    armed = false;
    window.removeEventListener('online', onOnline);
    document.removeEventListener('visibilitychange', onVisible);
  };
  const fire = (signal) => {
    if (!armed) return;
    disarm();
    trace('load_recovery_run', { signal });
    run();
  };
  const onOnline = () => fire('online');
  const onVisible = () => {
    if (document.hidden) return;
    // V8: a tab returning while STILL offline must not burn the one-shot on
    // a doomed attempt (up to ~21s of skeleton before the band returns).
    // navigator.onLine is a hint, but its `false` is reliable enough to
    // skip; the arm STAYS in place, so the later 'online' event (or a
    // visible-return once connectivity reports back) still gets its
    // recovery opportunity — an opportunity, not a guaranteed success.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    fire('visible');
  };
  window.addEventListener('online', onOnline);
  document.addEventListener('visibilitychange', onVisible);
  return disarm;
}

/**
 * Dashboard sections (Stage 3, 2026-10-31): a slow but still-progressing load
 * is NOT a failure. On cellular the Saved Pedals callable (cold start) and
 * the Trade Opportunities read chain ran past the 10 s per-attempt timeout
 * of runWithTimeoutAndRetry, which then started a SECOND concurrent attempt
 * and, when that also ran long, reported 'failed' and painted "Couldn't load
 * this section" while the first attempt was still on its way; its result
 * then landed underneath the error. This runner keeps ONE attempt in flight:
 *   - after `softMs` it calls `onSlow()` so the UI can say "still loading"
 *     (not an error);
 *   - a rejection (a real request failure) retries once after `retryDelayMs`;
 *   - only when nothing has settled by `hardMs` does it report 'failed'.
 * A result that arrives after 'failed' still reaches the caller's setter;
 * callers clear their failed flag in that setter when still current.
 * Returns 'ok' | 'failed' | 'superseded'.
 */
export async function runWithSoftTimeout(makeAttempt, { softMs, hardMs, retryDelayMs = 1000, isCurrent, label, onSlow }) {
  let slowTimer = null;
  let hardTimer = null;
  const clearTimers = () => { if (slowTimer) clearTimeout(slowTimer); if (hardTimer) clearTimeout(hardTimer); };
  const hardStop = new Promise((resolve) => { hardTimer = setTimeout(() => resolve('hard-timeout'), hardMs); });
  slowTimer = setTimeout(() => { if (isCurrent() && typeof onSlow === 'function') onSlow(); }, softMs);
  const attempt = async (fn, tag) => {
    try { await fn(); return 'ok'; } catch (error) { console.warn(`${label}${tag}: request failed`, error); return 'rejected'; }
  };
  try {
    let result = await Promise.race([attempt(makeAttempt, ''), hardStop]);
    if (result === 'rejected') {
      if (!isCurrent()) return 'superseded';
      await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
      if (!isCurrent()) return 'superseded';
      result = await Promise.race([attempt(makeAttempt, ':retry'), hardStop]);
    }
    if (result === 'ok') return 'ok';
    if (!isCurrent()) return 'superseded';
    trace('load_failed', { label, reason: result });
    return 'failed';
  } finally {
    clearTimers();
  }
}
